import React, { createContext, FC, PropsWithChildren, useMemo, useState } from "react";

import axios from "axios";
import { Helmet } from "react-helmet-async";

import ViewSDKClient from "@/shared/apis/adobe/ViewSDKClient";
import { tokenProvider } from "@/shared/hooks";

import BackdropWithSpinner from "../../components/atoms/Loader/BackdropWithSpinner/BackdropWithSpinner";
import ImageViewer from "../../components/molecules/ImageViewer/ImageViewer";

type MediaDisplayData = {
    title?: string;
    type?: string;
    url?: string | ArrayBuffer;
    elementId?: string;
    onClose?: () => void;
};

export interface IMediaDisplayerContext {
    displayFullMode: (obj: MediaDisplayData) => Promise<void>;
    displaySizedMode: (obj: MediaDisplayData) => Promise<void>;
}

export const MediaDisplayerContext = createContext<IMediaDisplayerContext>(null);
MediaDisplayerContext.displayName = "MediaDisplayerContext";

export const MediaDisplayerProvider: FC<PropsWithChildren> = ({ children }) => {
    const [mediaData, setMediaData] = useState<MediaDisplayData>(null);
    const [loading, setLoading] = useState(false);
    const [loadSdk, setLoadSdk] = useState(false);

    const handleDisplayFullMode = async ({ title, type, url, onClose }: MediaDisplayData) => {
        setLoadSdk(true);
        if (["image/jpg", "image/jpeg", "image/tiff", "image/png", "jpg", "png"].includes(type)) {
            setLoading(true);
            await axios
                .get<Blob>(url as string, {
                    responseType: "blob",
                    headers: {
                        Authorization: `Bearer ${tokenProvider.getToken()}`,
                    },
                })
                .then((res) => {
                    const reader = new window.FileReader();
                    reader.readAsDataURL(res.data);
                    reader.onload = () => {
                        setMediaData({ title, type, url: reader.result, onClose });
                    };
                });
            setLoading(false);
            return;
        }

        // Pdf
        const viewSDKClient = new ViewSDKClient(url, title, onClose);
        viewSDKClient.ready().then(() => {
            viewSDKClient.previewFile("", {
                embedMode: "LIGHT_BOX",
            });
        });
    };

    const handleDisplaySizedMode = async ({ title, url, elementId }: MediaDisplayData) => {
        setLoadSdk(true);
        // Pdf
        const viewSDKClient = new ViewSDKClient(url, title);
        viewSDKClient.ready().then(() => {
            viewSDKClient.previewFile(elementId, {
                embedMode: "SIZED_CONTAINER",
            });
        });
    };

    const contextValue = useMemo(
        (): IMediaDisplayerContext => ({
            displayFullMode: handleDisplayFullMode,
            displaySizedMode: handleDisplaySizedMode,
        }),
        [],
    );

    return (
        <MediaDisplayerContext.Provider value={contextValue}>
            {loadSdk && !window.AdobeDC && (
                <Helmet>
                    <script type="text/javascript" src="https://documentcloud.adobe.com/view-sdk/main.js"></script>
                </Helmet>
            )}
            {loading && <BackdropWithSpinner />}
            {/* Used for displaying images */}
            {mediaData && (
                <ImageViewer title={mediaData.title} url={mediaData.url} onClose={() => setMediaData(null)} />
            )}
            {children}
        </MediaDisplayerContext.Provider>
    );
};
